import { useMemo, useState, type CSSProperties } from 'react';
import { ancestorFolders, buildFileTree, MANIFEST, type TreeNode } from './fileTree';

/**
 * 最初に開くファイル。URLの指定が最優先で、無ければmanifestを開く。
 * パス順の先頭（client/client.js）はアプリの説明にならないので既定にしない。
 * 読み取り表示（CodeTab）と Draft の表示（DraftEditor）で同じ規則にする。
 */
export function pickFile(paths: string[], requested: string | null): string {
  if (requested && paths.includes(requested)) return requested;
  return paths.includes(MANIFEST) ? MANIFEST : (paths[0] as string);
}

export interface FileTreeProps {
  paths: string[];
  /** 表示中のファイル。ツリー上で現在地として示す。 */
  current: string;
  onSelectFile(path: string): void;
}

/**
 * IDEのエクスプローラのようなファイルツリー。並び順は buildFileTree が決める。
 *
 * 開閉の状態は「閉じたフォルダ」の集合で持つ。パッケージは小さいので、最初は
 * すべて開いた状態から始める。
 */
export function FileTree({ paths, current, onSelectFile }: FileTreeProps) {
  const tree = useMemo(() => buildFileTree(paths), [paths]);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());

  // 表示中のファイルが変わったら（戻る/進む・URLの直接指定）、祖先のフォルダを開き直す。
  // 閉じたままだと、開いているファイルがツリーのどこにも見えなくなる。effectで後から
  // 直すと1度閉じたまま描かれるので、描画中に前回のファイルと比べて直す。
  // 変わったときだけ直すので、表示中のファイルを含むフォルダも利用者は閉じられる。
  const [revealed, setRevealed] = useState(current);
  if (revealed !== current) {
    setRevealed(current);
    setCollapsed((folders) => withoutAncestors(folders, current));
  }

  const toggle = (folder: string) =>
    setCollapsed((folders) => {
      const next = new Set(folders);
      if (!next.delete(folder)) next.add(folder);
      return next;
    });

  return (
    <nav className="file-tree" aria-label="このmicroappのファイル">
      <TreeList
        nodes={tree}
        depth={0}
        current={current}
        collapsed={collapsed}
        onToggle={toggle}
        onSelectFile={onSelectFile}
      />
    </nav>
  );
}

/** 祖先がどれも閉じていなければ同じ集合を返し、余計な再描画を起こさない。 */
function withoutAncestors(collapsed: ReadonlySet<string>, path: string): ReadonlySet<string> {
  const ancestors = ancestorFolders(path).filter((folder) => collapsed.has(folder));
  if (ancestors.length === 0) return collapsed;
  const next = new Set(collapsed);
  for (const folder of ancestors) next.delete(folder);
  return next;
}

interface TreeListProps {
  nodes: TreeNode[];
  /** 入れ子の深さ。字下げとガイド線の位置は CSS が --tree-depth から計算する。 */
  depth: number;
  current: string;
  collapsed: ReadonlySet<string>;
  onToggle(folder: string): void;
  onSelectFile(path: string): void;
}

/** 1段ぶんの ul。フォルダが開いていれば、その中身を1段深い TreeList として入れ子にする。 */
function TreeList(props: TreeListProps) {
  const { nodes, depth, current, collapsed, onToggle, onSelectFile } = props;
  return (
    <ul className="tree-list" style={{ '--tree-depth': depth } as CSSProperties}>
      {nodes.map((node) => {
        if (node.kind === 'file') {
          const active = node.path === current;
          return (
            <li key={`file:${node.path}`}>
              <button
                type="button"
                className="tree-row"
                aria-current={active ? 'true' : undefined}
                // 行には名前だけを出す。読み上げと検索にはフルパスが要る。
                aria-label={node.path}
                onClick={() => onSelectFile(node.path)}
              >
                {/* 矢印の枠だけ空けて、同じ段のフォルダと名前の位置をそろえる。 */}
                <span className="tree-twistie" aria-hidden="true" />
                <FileIcon />
                <span className="tree-name">{node.name}</span>
              </button>
            </li>
          );
        }
        const open = !collapsed.has(node.path);
        return (
          <li key={`folder:${node.path}`}>
            <button
              type="button"
              className="tree-row tree-folder"
              aria-expanded={open}
              onClick={() => onToggle(node.path)}
            >
              <Chevron />
              <FolderIcon open={open} />
              <span className="tree-name">{node.name}</span>
            </button>
            {open && <TreeList {...props} nodes={node.children} depth={depth + 1} />}
          </li>
        );
      })}
    </ul>
  );
}

/** 開閉の矢印。右向きで描き、開いているときは CSS で下向きに回す。 */
function Chevron() {
  return (
    <span className="tree-twistie" aria-hidden="true">
      <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
        <path
          d="M6 4l4 4-4 4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function FolderIcon({ open }: { open: boolean }) {
  return (
    <span className="tree-icon" aria-hidden="true">
      <svg
        viewBox="0 0 16 16"
        width="14"
        height="14"
        focusable="false"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      >
        {open ? (
          // 開いたフォルダ。奥の面と、手前に傾いた面の2つで描く。
          <path d="M1.5 13V4.5A1.5 1.5 0 0 1 3 3h3l1.5 1.5H12a1.5 1.5 0 0 1 1.5 1.5V7M1.5 13l2.3-6h10.7l-2.3 6z" />
        ) : (
          <path d="M1.5 4.5A1.5 1.5 0 0 1 3 3h3l1.5 1.5H13a1.5 1.5 0 0 1 1.5 1.5v5.5A1.5 1.5 0 0 1 13 13H3a1.5 1.5 0 0 1-1.5-1.5z" />
        )}
      </svg>
    </span>
  );
}

function FileIcon() {
  return (
    <span className="tree-icon" aria-hidden="true">
      <svg
        viewBox="0 0 16 16"
        width="14"
        height="14"
        focusable="false"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      >
        <path d="M3.5 2.5a1 1 0 0 1 1-1h5l3 3v9a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1zM9.5 1.5v3h3" />
      </svg>
    </span>
  );
}
